import { useCallback, useEffect } from 'react';

import { subscriptionApi } from '@repo/subscription/api';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../../stores/planSelectionStore';
import { SupportedCurrency } from '../../../../types/SubscriptionPrice';
import {
  formatMonetaryValue,
  getMinimumCharge,
} from '../../../../utils/billing';
import { PromoCodeProps } from '../../../PromoCode/types';
import useBillingPrices from '../../hooks/useBillingPrices';
import { getPromoCodeFromCookie, priceWithPromoCode } from '../../utils';

interface PromoCodeManagement {
  onPromoCodeApplication: PromoCodeProps['onApplyClick'];
  onPromoCodeRemoval: PromoCodeProps['onRemoveClick'];
}

const getPromoCodeVerificationErrorMessage = (error: string): string => {
  return (
    {
      PROMO_CODE_NOT_FOUND: 'Your code is invalid.',
      PROMO_CODE_FIRST_TIME_TRANSACTION_ONLY:
        'This promo code is valid only for first-time purchases.',
      PROMO_CODE_MINIMUM_AMOUNT_NOT_REACHED:
        'Minimum order amount has not been reached.',
    }[error] ?? 'Failed to apply promo code.'
  );
};

const usePromoCodeManagement = (): PromoCodeManagement => {
  const { setPromoCode } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const prices = useBillingPrices();

  // Reset promo code when prices change. This is necessary because
  // not every promo code is applicable to every product.
  useEffect(() => {
    setPromoCode(null);
  }, [setPromoCode, prices]);

  const handlePromoCodeApplication = async (
    code: string
  ): Promise<{ success: true } | { success: false; error: string }> => {
    if (!prices) {
      throw new Error('prices must be provided');
    }

    const response = await subscriptionApi.verifyPromoCode(code, {
      localPrices: prices,
    });

    if (response.success) {
      const { success: _success, ...data } = response;

      const currency = prices[0]?.currency || SupportedCurrency.USD;
      const minimumCharge = getMinimumCharge(currency);
      const amountWithPromoCode = priceWithPromoCode(prices[0], data);

      const belowMinimumCharge =
        amountWithPromoCode.amount > 0 &&
        amountWithPromoCode.amount < minimumCharge;

      if (belowMinimumCharge) {
        return {
          success: false,
          error: `Applying this code would bring subtotal below the minimum chargeable amount (${formatMonetaryValue(
            {
              amount: minimumCharge,
              currency,
            }
          )}).`,
        };
      }

      setPromoCode(data);

      return { success: true };
    }

    return {
      success: false,
      error:
        'error' in response
          ? getPromoCodeVerificationErrorMessage(response.error)
          : 'Failed to apply promo code.',
    };
  };

  const handlePromoCodeApplicationCb = useCallback(handlePromoCodeApplication, [
    setPromoCode,
    prices,
  ]);

  useEffect(() => {
    const cookieCode = getPromoCodeFromCookie();
    if (cookieCode) {
      handlePromoCodeApplicationCb(cookieCode);
    } else {
      setPromoCode(null);
    }
  }, [handlePromoCodeApplicationCb, setPromoCode]);

  const handlePromoCodeRemoval = (): void => {
    setPromoCode(null);
  };

  return {
    onPromoCodeApplication: handlePromoCodeApplication,
    onPromoCodeRemoval: handlePromoCodeRemoval,
  };
};

export default usePromoCodeManagement;
